import { GLOBAL_FILTER_PANEL, LOCAL_FILTER_PANEL, TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import { appStateSelectors } from "../../modules/appState";
import { typeSelectors } from "../../modules/definition/types";
import { filterActionTypes, filterSelectors } from "../../modules/filters";
import { itemSelectors } from "../../modules/items";
import { relationshipSelectors } from "../../modules/relationships";
import preprocessorQuery from "../../utils/preprocessorQuery";
import {
  getFlashPlaceholder,
  getIconPlaceholder,
  getSwitchViewInstruction,
  getTipIdGenerator,
  getViewNameLabel,
  Placeholders
} from "../utils";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "FILTER_AND_COMPARE";
const GROUP_LABEL = "Filter and Compare";
const GROUP_DESCRIPTION = "Learn how to use filters to focus on key details or compare different data sets";
const GROUP_GATE = state =>
  typeSelectors.getSidebarTypes(state).size > 0 &&
  itemSelectors.getAllEventIds(state).size >= 5 &&
  relationshipSelectors.getRelationshipsById(state).size >= 3;
const GROUP_GATE_MESSAGE = `You must have an item type that is shown in the ${
  isMobile ? "toolbar" : "sidebar"
}, and at least 5 events and 3 relationships to begin this tutorial.`;
const GROUP_APPLICABLE_GATE = state => typeSelectors.getSidebarTypes(state).size > 0;
const GROUP_APPLICABLE_GATE_MESSAGE = `This tutorial only applies to timelines that have an item type that is shown in the ${
  isMobile ? "toolbar" : "sidebar"
}.`;
const GROUP_COMPLETED_MESSAGE = "Great work! Use what you have learned to focus in on your timeline as needed.";
const GROUP_LINKS = [
  { label: "Learn more about comparing data", path: "https://help.timeline.app/article/224-alternatives-to-group-by" },
  { label: "Global and Local Filters", path: "https://help.timeline.app/article/160-global-and-local-filters" }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("FILTER_TIMELINE_BY_ENTITY"),
      label: `See the timeline for a specific ${Placeholders.ENTITY_LABEL}`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by clicking its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Click on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_FILTER,
            "filter icon"
          )} ${getIconPlaceholder("workviewFilterFilled")} alongside ${Placeholders.AN_ENTITY_LABEL} name`,
          isNumbered: true
        },
        {
          label: `This will filter the focused view to only show ${Placeholders.EVENT_PLURAL_LABEL} related to that ${Placeholders.ENTITY_LABEL}.`,
          isNumbered: true
        },
        {
          label: `Click the active filter icon ${getIconPlaceholder(
            "workviewFilterFilledActive",
            "iconActive"
          )} again to remove the filter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === filterActionTypes.CHANGE_TYPE_REFERENCE_FILTER &&
        action.terms.length === 0
    },
    {
      id: idGenerator("COMPARE_TIMELINES"),
      label: `Compare the timelines for two ${Placeholders.ENTITY_PLURAL_LABEL}`,
      steps: [
        { label: `Open ${getViewNameLabel(TIMELINE_VIEW)} views in two split views`, isNumbered: true },
        { label: `Make the top view the focused split`, isNumbered: true },
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by clicking its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Click on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_FILTER,
            "filter icon"
          )} ${getIconPlaceholder("workviewFilterFilled")} alongside ${Placeholders.AN_ENTITY_LABEL} names`,
          isNumbered: true
        },
        {
          label: `Move focus to the next split view and click the filter icon next to a different ${Placeholders.ENTITY_LABEL}`,
          isNumbered: true
        },
        {
          label: `Each view will show only events related to the selected ${Placeholders.ENTITY_PLURAL_LABEL}`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => {
        const openViewsById = appStateSelectors.getOpenViewsById(state);
        const filters = filterSelectors.getFiltersById(state);
        const timelineViews = openViewsById.filter(v => v.viewType === TIMELINE_VIEW);
        return (
          timelineViews.size === 2 &&
          !timelineViews.find(v => {
            const filter = filters.get(v.viewId);
            return filter ? filter.relationshipsByType.size === 0 : true;
          })
        );
      }
    },
    {
      id: idGenerator("OPEN_FILTER_PANEL"),
      label: "See what filters are active",
      steps: [
        {
          label: `In the sidebar, click the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER,
            "Filter icon"
          )} ${getIconPlaceholder("toolbarFilter")} to open the filter panel.`,
          isNumbered: true
        },
        {
          label: `If any filters are applied globally to the entire timeline, the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER_PANEL_ALL_VIEWS,
            "All Views"
          )} tab will display the active filters.`,
          isNumbered: true
        },
        {
          label: `If any filters are applied locally to the focused view, the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER_PANEL_FOCUSED_VIEW,
            "Focused View"
          )} tab will display the active filters.`,
          isNumbered: true
        },
        {
          label: "Tip: If any filter is currently active, the Filter icon will display a dot indicator.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      dismissalGate: state => {
        const leftPanelMode = appStateSelectors.getLeftPanelMode(state);
        return leftPanelMode === GLOBAL_FILTER_PANEL || leftPanelMode === LOCAL_FILTER_PANEL;
      }
    },
    {
      id: idGenerator("FILTER_ALL_VIEWS"),
      label: "Filter all views by item color",
      steps: [
        {
          label: `In the sidebar, click the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER,
            "Filter icon"
          )} ${getIconPlaceholder("toolbarFilter")} to open the filter panel.`,
          isNumbered: true
        },
        {
          label: `Ensure the selected tab is ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER_PANEL_ALL_VIEWS,
            "All Views"
          )}`,
          isNumbered: true
        },
        { label: "Click the Color option", isNumbered: true },
        { label: "Tick a color to filter all views to show only items of that color.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) => {
        const filters = filterSelectors.getFiltersById(newState);
        return filters.find(f => f.color.terms.size > 0);
      }
    }
  ];
} else if (isTablet) {
  tips = [
    {
      id: idGenerator("FILTER_TIMELINE_BY_ENTITY"),
      label: `See the timeline for a specific ${Placeholders.ENTITY_LABEL}`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_FILTER,
            "filter icon"
          )} ${getIconPlaceholder("workviewFilterFilled")} alongside ${Placeholders.AN_ENTITY_LABEL} name`,
          isNumbered: true
        },
        {
          label: `This will filter the focused view to only show ${Placeholders.EVENT_PLURAL_LABEL} related to that ${Placeholders.ENTITY_LABEL}.`,
          isNumbered: true
        },
        {
          label: `Tap the active filter icon ${getIconPlaceholder(
            "workviewFilterFilledActive",
            "iconActive"
          )} again to remove the filter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      requiresExitEditMode: true,
      requiresInspector: true,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === filterActionTypes.CHANGE_TYPE_REFERENCE_FILTER &&
        action.terms.length === 0
    },
    {
      id: idGenerator("COMPARE_TIMELINES"),
      label: `Compare the timelines for two ${Placeholders.ENTITY_PLURAL_LABEL}`,
      steps: [
        { label: `Open ${getViewNameLabel(TIMELINE_VIEW)} views in two split views`, isNumbered: true },
        { label: `Make the top view the focused split`, isNumbered: true },
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_FILTER,
            "filter icon"
          )} ${getIconPlaceholder("workviewFilterFilled")} alongside ${Placeholders.AN_ENTITY_LABEL} names`,
          isNumbered: true
        },
        {
          label: `Move focus to the next split view and tap the filter icon next to a different ${Placeholders.ENTITY_LABEL}`,
          isNumbered: true
        },
        {
          label: `Each view will show only events related to the selected ${Placeholders.ENTITY_PLURAL_LABEL}`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresExitEditMode: true,
      requiresInspector: true,
      dismissalGate: state => {
        const openViewsById = appStateSelectors.getOpenViewsById(state);
        const filters = filterSelectors.getFiltersById(state);
        const timelineViews = openViewsById.filter(v => v.viewType === TIMELINE_VIEW);
        return (
          timelineViews.size === 2 &&
          !timelineViews.find(v => {
            const filter = filters.get(v.viewId);
            return filter ? filter.relationshipsByType.size === 0 : true;
          })
        );
      }
    },
    {
      id: idGenerator("OPEN_FILTER_PANEL"),
      label: "See what filters are active",
      steps: [
        {
          label: `In the sidebar, tap the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER,
            "Filter icon"
          )} ${getIconPlaceholder("toolbarFilter")} to open the filter panel.`,
          isNumbered: true
        },
        {
          label: `If any filters are applied globally to the entire timeline, the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER_PANEL_ALL_VIEWS,
            "All Views"
          )} tab will display the active filters.`,
          isNumbered: true
        },
        {
          label: `If any filters are applied locally to the focused view, the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER_PANEL_FOCUSED_VIEW,
            "Focused View"
          )} tab will display the active filters.`,
          isNumbered: true
        },
        {
          label: "Tip: If any filter is currently active, the Filter icon will display a dot indicator.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true,
      dismissalGate: state => {
        const leftPanelMode = appStateSelectors.getLeftPanelMode(state);
        return leftPanelMode === GLOBAL_FILTER_PANEL || leftPanelMode === LOCAL_FILTER_PANEL;
      }
    },
    {
      id: idGenerator("FILTER_ALL_VIEWS"),
      label: "Filter all views by item color",
      steps: [
        {
          label: `In the sidebar, tap the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER,
            "Filter icon"
          )} ${getIconPlaceholder("toolbarFilter")} to open the filter panel.`,
          isNumbered: true
        },
        {
          label: `Ensure the selected tab is ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER_PANEL_ALL_VIEWS,
            "All Views"
          )}`,
          isNumbered: true
        },
        { label: "Expand the Color option", isNumbered: true },
        { label: "Enable a color to filter all views to show only items of that color.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresInspector: true,
      resolve: (action, newState) => {
        const filters = filterSelectors.getFiltersById(newState);
        return filters.find(f => f.color.terms.size > 0);
      }
    }
  ];
} else if (isMobile) {
  tips = [
    {
      id: idGenerator("FILTER_TIMELINE_BY_ENTITY"),
      label: `See the timeline for a specific ${Placeholders.ENTITY_LABEL}`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the footer.`,
          isNumbered: true
        },
        {
          label: `Tap on the filter icon ${getIconPlaceholder("workviewFilterFilled")} alongside ${
            Placeholders.AN_ENTITY_LABEL
          } name`,
          isNumbered: true
        },
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `The timeline will be filtered to only show ${Placeholders.EVENT_PLURAL_LABEL} related to that ${Placeholders.ENTITY_LABEL}.`,
          isNumbered: true
        },
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel and tap the active filter icon ${getIconPlaceholder(
            "workviewFilterFilledActive",
            "iconActive"
          )} again to remove the filter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      requiresExitEditMode: true,
      requiresInspector: true,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === filterActionTypes.CHANGE_TYPE_REFERENCE_FILTER &&
        action.terms.length === 0
    },
    {
      id: idGenerator("FILTER_ALL_VIEWS"),
      label: "Filter by item color",
      steps: [
        {
          label: `In the toolbar, tap the ${getFlashPlaceholder(
            interfaceIdentifiers.FILTER,
            "Filter icon"
          )} ${getIconPlaceholder("toolbarFilter")} to open the filter panel.`,
          isNumbered: true
        },
        { label: "Expand the Color option", isNumbered: true },
        { label: "Enable a color to filter all views to show only items of that color.", isNumbered: true },
        {
          label: "Tip: If any filter is currently active, the Filter icon will display a dot indicator.",
          isNumbered: false
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresInspector: true,
      resolve: (action, newState) => {
        const filters = filterSelectors.getFiltersById(newState);
        return filters.find(f => f.color.terms.size > 0);
      }
    }
  ];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
